import BaseConfiguratorService from '@configuratorware/configurator-frontendgui/App/Services/ConfiguratorService';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { getFontMapping } from '@configuratorware/configurator-frontendgui/App/Reducers/Fonts/Selectors';
import {
  prepareDesignDataSnapshots,
  preparePrintDesignData,
} from '@configuratorware/configurator-frontendgui/App/Modules/Designer/Utils/SvgFixers';
import {
  getItem,
  getSelectedAmounts,
  getSelectedVariantIdentifier,
  isCalculationAutoResolveEnabled,
} from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import { setCalculationEnabled } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import * as configuratorActions from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Actions';
import get from 'lodash/get';
import { showTwoSegmentsDialog } from '../Reducers/HintMessages/Actions';

const getSelectedAmountsBySizes = (item, selectedAmounts) => {
  const itemGroupMap = (rootItem, fn) => {
    const data = [];
    if (get(rootItem, 'itemGroup.children.length', 0) > 0) {
      for (let item of rootItem.itemGroup.children) {
        const mappedData = fn(item, data);
        mappedData && data.push(mappedData);
      }
    }
    return data;
  };

  return itemGroupMap(item, (colorGroupItem) => ({
    identifier: colorGroupItem.identifier,
    amount: get(selectedAmounts, colorGroupItem.identifier, 0),
    sizes: itemGroupMap(colorGroupItem, (sizeGroupItem) => ({
      amount: get(selectedAmounts, sizeGroupItem.identifier, 0),
      identifier: sizeGroupItem.identifier,
    })),
  }));
};

export default class ConfiguratorService extends BaseConfiguratorService {
  async createMediaDataForConfiguration(configuration) {
    const mediaData = {};
    const restorePlaceholderVisibility = this.togglePlaceholders(false);
    const memo = Services.designData.createHighlightMemo(false);

    const fontMapping = getFontMapping(Services.store.state);

    const svgs = await Services.designData.getScreenshotsOfElements(fontMapping);
    mediaData.designDataSnapshots = prepareDesignDataSnapshots(svgs);

    const fullSvgs = await Services.designData.getScreenshots(fontMapping);
    mediaData.printDesignData = preparePrintDesignData(fullSvgs);
    const usedAreas = Object.keys(fullSvgs);

    const item = getItem(this.state);
    const selectedAmounts = getSelectedAmounts(this.state);

    const selectedVariants = getSelectedAmountsBySizes(item, selectedAmounts)
      .filter((item) => {
        if (item.sizes.length > 0) {
          for (const size of item.sizes) {
            if (size.amount > 0) {
              return true;
            }
          }
        } else {
          if (item.amount > 0) {
            return true;
          }
        }

        return false;
      })
      .map((item) => item.identifier);

    let selectedVariant = getSelectedVariantIdentifier(this.state);

    if (selectedVariants.length === 0) {
      selectedVariants.push(selectedVariant);
    }
    // LAN-17: Adding all the designviews to the screenshots for zip pdf file
    const designViewList = Services.store.state.designView.designViews;

    mediaData.screenshots = {};

    try {
      const screenshots = await Services.visualization.getScreenshots({
        areaList: usedAreas, // used in 2D visualization
        viewList: designViewList, // used in 3D visualization
        variantList: selectedVariants,
        designViewList, // used in 2D visualization
      });

      if (screenshots) {
        // make sure the order is kept on the server side
        for (const variant of selectedVariants) {
          const variantImages = screenshots[variant];
          if (variantImages) {
            const keys = Object.keys(variantImages);
            const orderedImages = {};
            for (let index = 0; index < keys.length; index++) {
              const key = keys[index];
              orderedImages[index + key] = variantImages[key];
            }
            screenshots[variant] = orderedImages;
          }
        }
      }

      mediaData.screenshots = screenshots;
    } catch (e) {
      // do not save in case of an error while generating screenshots
      // eslint-disable-next-line no-console
      console.error('Error occurred while generating screenshots:', e);
    }
    memo.restore();

    if (typeof configuration === 'object') {
      Object.assign(configuration, mediaData);
    }
    restorePlaceholderVisibility();
    return mediaData;
  }

  async onConfigurationChange(configuration, prevConfiguration) {
    if (
      this.state.calculationIsEnabled &&
      (!configuration.check_results || // no check results
        configuration.check_results.status) && // or the check result status is ok
      this.shouldInvalidateCalculation(prevConfiguration, configuration)
    ) {
      if (isCalculationAutoResolveEnabled(this.state)) {
        // disable automatic price updates to take direct control over error handling
        Services.store.dispatch(setCalculationEnabled(false));
        // triggering the calculation directly to make sure it's synced
        await Services.store.dispatch(configuratorActions.doCalculation());
        Services.store.dispatch(setCalculationEnabled(true));
      } else {
        Services.store.dispatch(configuratorActions.invalidateCalculation());
        Services.store.dispatch(configuratorActions.doCalculation());
      }
    }

    if (this.shouldUpdateVisualization(prevConfiguration, configuration)) {
      Services.visualization.update(configuration);
    }

    const designData = get(configuration, 'designdata', {});
    let objectCount = 0;
    if (
      Services.ui.isVisible('designer') &&
      Services.store.state.configurator.configuration.configurationType !== 'user'
    ) {
      if (designData) {
        for (const [key, value] of Object.entries(designData)) {
          //@ts-ignore
          if (key.includes('segment') && value.canvasData?.objects && value.canvasData.objects?.length > 0) {
            objectCount++;
          }
        }
      }
      if (objectCount >= 2 && !Services.store.state.hintMessages.twoSegmentsDialogShown) {
        Services.store.dispatch(showTwoSegmentsDialog());
      }
    }
  }
}
